// src/services/catalogImportService.ts
// Catalog Migration cockpit — drives the smart catalog import module:
// ingest (Excel → staged rows) → select → materialize (→ Parts) → release
// (datasheet acquisition), with run/pause/stop controls and live stats.
// Talks to the catalog `/catalog-import` endpoints.

import apiClient from './api';

const ROOT = '/catalog-import';

export interface CatalogJobStats {
  total: number;
  byTier: Record<string, number>;
  byStatus: Record<string, number>;
  controlState?: string;
  releaseActive?: boolean;
}

export interface CatalogRow {
  _id: string;
  materialNumber: string;
  partNumber?: string;
  manufacturerPartNumber?: string;
  manufacturerName?: string;
  readinessTier: string;
  status: string;
  partId?: string;
  discovery?: {
    status?: string;
    pdfRegistered?: boolean;
    host?: string;
    error?: string;
  };
}

export interface IngestResult {
  jobId: string;
  totalRows: number;
  clarificationRows: number;
  tiers: Record<string, number>;
  duplicateFlagged: number;
}

export interface MappingSuggestion {
  targetField: string;
  sourceColumn: string;
  confidence: number;
}
export interface PreviewResult {
  filePath: string;
  originalFileName: string;
  sheetName: string;
  columns: string[];
  mapping: MappingSuggestion[];
  sample: Array<Record<string, any>>;
  totalRows: number;
  detected: {
    attributePairs: number;
    attributeExamples: string[];
    referenceUrlColumns: number;
    classificationColumns: number;
  };
}

export interface JobSummary {
  jobId: string;
  jobName?: string;
  originalFileName?: string;
  status: string;
  currentStep?: string;
  createdAt: string;
  stats?: { totalRows?: number };
}

const unwrap = <T>(d: { data: T }): T => d.data;

export const catalogImportService = {
  /** Recent catalog-import jobs (for the cockpit job picker). */
  async listJobs(): Promise<JobSummary[]> {
    const { data } = await apiClient.get(`${ROOT}/jobs`);
    return unwrap(data);
  },

  /** Delete an import job + its staged rows (materialized Parts are kept). */
  async deleteJob(jobId: string): Promise<{ deleted: string; rowsDeleted: number }> {
    const { data } = await apiClient.delete(`${ROOT}/jobs/${jobId}`);
    return unwrap(data);
  },

  /** Upload a file → auto-detect mapping + sample (no DB writes). */
  async uploadPreview(file: File, sheetName?: string): Promise<PreviewResult> {
    const form = new FormData();
    form.append('file', file);
    if (sheetName) form.append('sheetName', sheetName);
    // Override the client's default application/json so axios/the browser sets
    // the multipart/form-data boundary — otherwise the file never parses.
    const { data } = await apiClient.post(`${ROOT}/upload-preview`, form, {
      headers: { 'Content-Type': undefined as unknown as string },
    });
    return unwrap(data);
  },

  /** Stage an uploaded file with the confirmed mapping → returns jobId. */
  async uploadIngest(body: {
    filePath: string;
    originalFileName?: string;
    jobName?: string;
    sheetName?: string;
    mapping?: Record<string, string>;
  }): Promise<IngestResult> {
    const { data } = await apiClient.post(`${ROOT}/upload-ingest`, body);
    return unwrap(data);
  },

  async ingest(body: {
    tenantId: string;
    organizationId: string;
    createdBy: string;
    filePath: string;
    jobName?: string;
  }): Promise<IngestResult> {
    const { data } = await apiClient.post(`${ROOT}/ingest`, body);
    return unwrap(data);
  },

  async stats(jobId: string): Promise<CatalogJobStats> {
    const { data } = await apiClient.get(`${ROOT}/jobs/${jobId}/stats`);
    return unwrap(data);
  },

  async rows(
    jobId: string,
    params: { tier?: string; status?: string; limit?: number; offset?: number; search?: string } = {},
  ): Promise<{ rows: CatalogRow[]; total: number }> {
    const { data } = await apiClient.get(`${ROOT}/jobs/${jobId}/rows`, { params });
    return unwrap(data);
  },

  async select(
    jobId: string,
    body: { tier?: string; firstN?: number; rowIds?: string[]; materialNumbers?: string[]; hasPdf?: boolean },
  ): Promise<{ selected: number }> {
    const { data } = await apiClient.post(`${ROOT}/jobs/${jobId}/select`, body);
    return unwrap(data);
  },

  async materialize(
    jobId: string,
    body: { limit?: number; rowIds?: string[]; tier?: string; search?: string; all?: boolean } = {},
  ): Promise<{ materialized?: number; partIds?: string[]; started?: boolean; matching?: number; background?: boolean }> {
    const { data } = await apiClient.post(`${ROOT}/jobs/${jobId}/materialize`, body);
    return unwrap(data);
  },

  async release(
    jobId: string,
    body: {
      limit?: number; concurrency?: number; fullLadder?: boolean; retryMisses?: boolean;
      rowIds?: string[]; tier?: string; search?: string; all?: boolean;
    } = {},
  ): Promise<{
    released?: number; registered?: number; misses?: number; stoppedEarly?: boolean;
    started?: boolean; matching?: number; background?: boolean;
  }> {
    const { data } = await apiClient.post(`${ROOT}/jobs/${jobId}/release`, body);
    return unwrap(data);
  },

  async control(jobId: string, action: 'pause' | 'resume' | 'stop'): Promise<{ controlState: string }> {
    const { data } = await apiClient.post(`${ROOT}/jobs/${jobId}/${action}`);
    return unwrap(data);
  },
};

export default catalogImportService;
